import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, IndianRupee, Loader2 } from "lucide-react";
import { v2CtcAllocationPreview } from "../../SalaryApiV2";
import {
  buildFormulaOverridesFromPipeline,
  ctcStatusTone,
  formatInr,
} from "./pipelineCtcUtils";
import { readPipelineState } from "./salaryTemplatePipeline";

function formulaLoopCode(raw) {
  const text = String(raw || "").trim();
  const cycle = text.match(/circular dependency detected at\s+(.+)$/i);
  if (!cycle) return "";
  return cycle[1].replace(/^comp:/i, "").trim();
}

/**
 * CTC allocation card — calculations always from backend salary_engine_v2.
 */
export default function PipelineCtcSection({
  compact = false,
  templateId = null,
  componentIds = null,
  onTargetChange,
}) {
  const [targetAnnual, setTargetAnnual] = useState(() => {
    const v = readPipelineState().targetAnnualCtc;
    return v != null && v !== "" ? String(v) : "";
  });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const ids = useMemo(() => {
    if (componentIds?.length) return componentIds;
    return readPipelineState().allocatedComponentIds || [];
  }, [componentIds]);

  const runPreview = useCallback(async () => {
    const annual = Number(String(targetAnnual || "").replace(/,/g, ""));
    if (!Number.isFinite(annual) || annual <= 0) {
      setPreview(null);
      setError("");
      return;
    }
    if (!templateId && (!ids || !ids.length)) {
      setError("Allocate components before previewing gross salary.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const formula_overrides = buildFormulaOverridesFromPipeline();
      const templateUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(templateId || ""))
        ? String(templateId)
        : "";
      const componentIds = (ids || [])
        .map((id) => String(id || "").trim())
        .filter((id) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id));
      const payload = {
        template_id: templateUuid || undefined,
        component_ids: componentIds.length ? componentIds : undefined,
        formula_overrides,
        annual_ctc: annual,
      };
      let data;
      try {
        data = await v2CtcAllocationPreview(payload);
      } catch (first) {
        const msg = String(first?.detail || first?.message || "");
        const missingTemplate = /template not found|has no components/i.test(msg);
        if (!(missingTemplate && componentIds.length)) throw first;
        data = await v2CtcAllocationPreview({
          component_ids: componentIds,
          formula_overrides,
          annual_ctc: annual,
        });
      }
      setPreview(data);
      onTargetChange?.(annual, data);
    } catch (e) {
      const msg = String(e?.detail || e?.message || "");
      const missingTemplate = /template not found|has no components/i.test(msg);
      setPreview(null);
      setError(missingTemplate && ids?.length ? "" : (e?.detail || e?.message || "Could not preview gross salary"));
    } finally {
      setLoading(false);
    }
  }, [targetAnnual, templateId, ids, onTargetChange]);

  useEffect(() => {
    const t = setTimeout(runPreview, 450);
    return () => clearTimeout(t);
  }, [runPreview]);

  const monthlyTarget =
    Number(targetAnnual) > 0 ? Math.round(Number(targetAnnual) / 12) : null;

  // Guard: never let employer PF/charges count toward Gross (even if API is stale).
  const displayPreview = useMemo(() => {
    if (!preview) return null;
    const isEmployerRow = (row) => {
      const code = String(row?.component_code || "").toUpperCase().replace(/\s+/g, "_");
      const name = String(row?.name || "").toLowerCase();
      const cat = String(row?.category || "").toLowerCase();
      if (cat.includes("employer")) return true;
      if (
        [
          "PF_EMPLOYER",
          "EMP_PF",
          "EMPLOYER_PF",
          "ER_PF",
          "PF_ADMIN",
          "PF_EDLI",
          "PF_EPS",
          "PF_EPF",
          "ESI_EMPLOYER",
          "EMP_ESI",
          "ER_ESI",
          "LWF_EMPLOYER",
          "EMP_LWF",
        ].includes(code)
      ) {
        return true;
      }
      return name.includes("employer") && /(pf|provident|esi|lwf|epf)/.test(name);
    };

    const fromApiEmployer = Array.isArray(preview.employer_contribution_lines)
      ? preview.employer_contribution_lines
      : [];
    const earnings = [];
    const employer = [...fromApiEmployer];
    for (const row of preview.component_lines || []) {
      if (isEmployerRow(row)) employer.push(row);
      else earnings.push(row);
    }

    const monthlyGross = earnings.reduce((s, r) => s + (Number(r.monthly_amount) || 0), 0);
    const annualGross = Math.round(monthlyGross * 12 * 100) / 100;
    const target = Number(preview.target_annual_ctc) || 0;
    const diff = annualGross - target;
    const tol = 1;
    let status = "balanced";
    let message = "Gross fully allocated";
    if (Math.abs(diff) > tol) {
      if (diff < 0) {
        status = "under";
        message = `₹${Math.abs(diff).toLocaleString("en-IN")} remaining to allocate`;
      } else {
        status = "over";
        message = `Package exceeds target Gross by ₹${Math.abs(diff).toLocaleString("en-IN")}`;
      }
    }

    return {
      ...preview,
      component_lines: earnings,
      employer_contribution_lines: employer,
      calculated_monthly_ctc: monthlyGross,
      calculated_annual_ctc: annualGross,
      difference_annual: diff,
      difference_monthly: diff / 12,
      status,
      message,
    };
  }, [preview]);

  const tone = ctcStatusTone(displayPreview?.status);

  return (
    <section className={`stp-ctc-card${compact ? " stp-ctc-card--compact" : ""}`}>
      <div className="stp-ctc-head">
        <IndianRupee size={18} aria-hidden />
        <div>
          <h3 className="stp-ctc-title">Gross salary</h3>
          <p className="stp-section-hint">
            Set the annual gross salary for this structure. Amounts are calculated by the payroll engine.
          </p>
        </div>
      </div>

      <div className="stp-ctc-grid">
        <label className="stp-label" htmlFor="stp-target-ctc">
          Annual gross salary
        </label>
        <input
          id="stp-target-ctc"
          className="stp-input"
          inputMode="numeric"
          placeholder="e.g. 600000"
          value={targetAnnual}
          onChange={(e) => setTargetAnnual(e.target.value)}
          aria-describedby="stp-ctc-hint"
        />
        <p id="stp-ctc-hint" className="stp-section-hint">
          Annual gross salary is divided by 12 for the monthly gross used in formulas.
        </p>
      </div>

      <div className="stp-ctc-kpis">
        <div className="stp-ctc-kpi">
          <span>Annual gross salary</span>
          <strong>{monthlyTarget != null ? formatInr(targetAnnual) : "—"}</strong>
        </div>
        <div className="stp-ctc-kpi">
          <span>Monthly gross salary</span>
          <strong>{monthlyTarget != null ? formatInr(monthlyTarget) : "—"}</strong>
        </div>
      </div>

      {loading ? (
        <p className="stp-ctc-loading">
          <Loader2 size={16} className="stp-spin" /> Calculating package…
        </p>
      ) : null}
      {error ? <p className="stp-error">{error}</p> : null}

      {displayPreview ? (
        <>
          <div className="stp-ctc-lines">
            <div className="stp-ctc-lines-head">Earnings (count toward Gross)</div>
            <ul className="stp-ctc-line-list">
              {(displayPreview.component_lines || []).map((row) => (
                <li key={row.component_id || row.component_code}>
                  <span>{row.name}</span>
                  <span>{formatInr(row.monthly_amount)}/mo</span>
                </li>
              ))}
              {!displayPreview.component_lines?.length ? (
                <li className="stp-section-hint">No earning amounts returned.</li>
              ) : null}
            </ul>
          </div>

          {displayPreview.employer_contribution_lines?.length ? (
            <div className="stp-ctc-lines">
              <div className="stp-ctc-lines-head">Employer contributions (not in Gross)</div>
              <ul className="stp-ctc-line-list">
                {displayPreview.employer_contribution_lines.map((row) => (
                  <li key={row.component_id || row.component_code}>
                    <span>{row.name}</span>
                    <span>{formatInr(row.monthly_amount)}/mo</span>
                  </li>
                ))}
              </ul>
              <p className="stp-section-hint" style={{ marginTop: "0.35rem" }}>
                Controlled by Statutory PF Gross Treatment. These amounts do not change the Gross balance above.
              </p>
            </div>
          ) : null}

          <div className={`stp-ctc-summary stp-ctc-summary--${tone}`}>
            <div className="stp-ctc-summary-row">
              <span>Calculated gross salary</span>
              <strong>{formatInr(displayPreview.calculated_annual_ctc)}</strong>
            </div>
            <div className="stp-ctc-summary-row">
              <span>Target gross salary</span>
              <strong>{formatInr(displayPreview.target_annual_ctc)}</strong>
            </div>
            <div className="stp-ctc-summary-row">
              <span>Difference</span>
              <strong>
                {displayPreview.difference_annual >= 0 ? "+" : ""}
                {formatInr(displayPreview.difference_annual)}
              </strong>
            </div>
            <div className="stp-ctc-status">
              {tone === "good" ? (
                <CheckCircle2 size={16} />
              ) : (
                <AlertTriangle size={16} />
              )}
              <span>{String(displayPreview.message || "").replace(/\bCTC\b/gi, "Gross")}</span>
            </div>
          </div>
          {displayPreview.errors?.length ? (
            formulaLoopCode(displayPreview.errors[0]) ? (
              <div className="stp-guidance">
                <p className="stp-guidance-title">How to fix this</p>
                <p>
                  The component <strong>{formulaLoopCode(displayPreview.errors[0])}</strong> and another component are using each other in their formulas. Payroll cannot decide which amount to calculate first, so every amount on this page stays at zero.
                </p>
                <ol>
                  <li>Press <strong>Back to formulas</strong>.</li>
                  <li>Open the component whose code is <strong>{formulaLoopCode(displayPreview.errors[0])}</strong>.</li>
                  <li>Look at its formula and note any other component it uses, such as Basic or Special Allowance.</li>
                  <li>Open that other component. If its formula also contains <strong>{formulaLoopCode(displayPreview.errors[0])}</strong>, that is the loop.</li>
                  <li>Change only one of those two formulas. Use gross salary or a fixed number, and remove the other component from it.</li>
                  <li>Press <strong>Save formula</strong>, then return here. The amounts will calculate once the loop is gone.</li>
                </ol>
              </div>
            ) : (
              <p className="stp-guidance">{String(displayPreview.errors[0])}</p>
            )
          ) : null}
        </>
      ) : null}
    </section>
  );
}
